import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Modal, ActionIcon, Text, Group, Stack, NumberInput, Switch } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlay, 
  faPause, 
  faUpload, 
  faTrash, 
  faCut, 
  faCopy, 
  faVolumeUp, 
  faVolumeMute,
  faSearchPlus,
  faSearchMinus,
  faDownload,
  faArrowDown,
  faStop,
  faMagic,
  faSave,
  faSliders,
  faArrowLeft,
  faPencilAlt,
  faMagicWandSparkles
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/Auth/AuthContext';
import { getApiUrl } from '../../config/api';
import Navigation from '../Home/Navigation';
import VerticalNav from '../Home/VerticalNav';
import AudioMixer from './AudioMixer';
import TabContainer from './components/TabContainer';
import SoundEffectsModal from './SoundEffectsModal';
import AudioModal from './AudioModal';
import AudioSelectionModal from './AudioSelectionModal';
import styles from './AudioEditor.module.css';
import { useNotification } from '../../context/NotificationContext';
import { StepIndicatorNames } from '../../types';
import { AudioTrack } from './types';
import { loadAuthenticatedAudioBuffer } from '../../utils/audioProxyUtil';
import { TrackActivationService } from '../../services/trackActivationService';
import { DurationUtils } from './utils/durationUtils';
import AudioEditorHeader from './components/AudioEditorHeader';
import AudioControlBar from './components/AudioControlBar';
import { fillAudioGaps } from './services/fillAudioGaps';

// Helper functions for audio fade calculations
const calculateVolumeWithFades = (
  currentTime: number, 
  duration: number, 
  baseVolume: number, 
  fadeInDuration: number, 
  fadeOutDuration: number
): number => {
  let volume = baseVolume;
  
  // Apply fade in
  if (fadeInDuration > 0 && currentTime < fadeInDuration) {
    const fadeInProgress = currentTime / fadeInDuration;
    volume *= fadeInProgress;
  }
  
  // Apply fade out
  if (fadeOutDuration > 0 && currentTime > (duration - fadeOutDuration)) {
    const fadeOutStart = duration - fadeOutDuration;
    const fadeOutProgress = (currentTime - fadeOutStart) / fadeOutDuration;
    volume *= (1 - fadeOutProgress);
  }
  
  return Math.max(0, Math.min(volume, baseVolume));
};

const scheduleFadeAutomation = (
  gainNode: GainNode, 
  startTime: number, 
  track: AudioTrack, 
  when: number
) => {
  const { duration, volume: baseVolume, fadeInDuration, fadeOutDuration } = track;
  
  // Clear any existing automation
  gainNode.gain.cancelScheduledValues(when);
  
  // Set current volume
  const currentVolume = calculateVolumeWithFades(startTime, duration, baseVolume, fadeInDuration, fadeOutDuration);
  gainNode.gain.setValueAtTime(currentVolume, when);
  
  // Schedule fade in if we're still in fade in period
  if (fadeInDuration > 0 && startTime < fadeInDuration) {
    const fadeInEnd = when + (fadeInDuration - startTime);
    gainNode.gain.linearRampToValueAtTime(baseVolume, fadeInEnd);
  }
  
  // Schedule fade out
  if (fadeOutDuration > 0) {
    const fadeOutStart = duration - fadeOutDuration;
    if (startTime < fadeOutStart) {
      // We haven't reached fade out yet
      const fadeOutStartTime = when + (fadeOutStart - startTime);
      const fadeOutEndTime = fadeOutStartTime + fadeOutDuration;
      gainNode.gain.setValueAtTime(baseVolume, fadeOutStartTime);
      gainNode.gain.linearRampToValueAtTime(0, fadeOutEndTime);
    }
  }
};



interface AudioEditorSplitProps {
  // No props needed - we'll get projectId from URL params
}

const AudioEditorSplit: React.FC<AudioEditorSplitProps> = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate(); // Add navigate hook
  const audioUrl = searchParams.get('audioUrl');
  const { user, token } = useAuth(); // Add auth hook to get user ID and token
  
  // Utility functions - Define early to avoid hoisting issues
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1);
    return `${mins}:${secs.padStart(4, '0')}`;
  };

  const snapToGridValue = (value: number, gridSizeParam: number, snapEnabled: boolean): number => {
    if (!snapEnabled) return value;
    return Math.round(value / gridSizeParam) * gridSizeParam;
  };
  
  // Audio context and state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(60);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pixelsPerSecond, setPixelsPerSecond] = useState(50); // Fixed pixels per second for proper zoom
  const [tracks, setTracks] = useState<AudioTrack[]>([]);
  
  // ✅ Helper function to ensure tracks have valid lane indexes
  const setTracksWithValidLanes = (newTracks: AudioTrack[] | ((prev: AudioTrack[]) => AudioTrack[])) => {
    if (typeof newTracks === 'function') {
      setTracks((prev) => {
        const updated = newTracks(prev);
        return updated.map((track, index) => ({
          ...track,
          laneIndex: index % 3 // ✅ Force redistribution by index to ensure even spread
        }));
      });
    } else {
      const validatedTracks = newTracks.map((track, index) => ({
        ...track,
        laneIndex: index % 3 // ✅ Force redistribution by index to ensure even spread
      }));
      console.log('🔧 FORCING TRACK REDISTRIBUTION:', validatedTracks.map((t, index) => ({
        name: t.name.substr(0, 20),
        oldLane: newTracks[index]?.laneIndex,
        newLane: t.laneIndex
      })));
      setTracks(validatedTracks);
    }
  };
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [activeTrackId, setActiveTrackId] = useState<string | null>(null);
  const [splitAutoSave, setSplitAutoSave] = useState<{wasOriginalTrackActive: boolean, firstTrack: AudioTrack} | null>(null); // ✅ NEW: Lazy loading state
  const [trackLoadStates, setTrackLoadStates] = useState<Map<string, 'ACTIVE' | 'LOADING' | 'DORMANT' | 'ERROR'>>(new Map()); // ✅ NEW
  const [isLoading, setIsLoading] = useState(false);
  const [draggedTrack, setDraggedTrack] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartY, setDragStartY] = useState(0);
  const [resizeMode, setResizeMode] = useState<'left' | 'right' | null>(null);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(1); // seconds
  const [masterVolume, setMasterVolume] = useState(1);
  const [isMasterMuted, setIsMasterMuted] = useState(false);
  const [showAudioMixer, setShowAudioMixer] = useState(false);
  const [showSoundEffectsModal, setShowSoundEffectsModal] = useState(false);
  const [soundEffectsRefreshTrigger, setSoundEffectsRefreshTrigger] = useState(0);
  const [shouldNavigateToContentCreation, setShouldNavigateToContentCreation] = useState(false);
  const [autoCenter, setAutoCenter] = useState(false); // Auto-center playhead state
  
  // AI Audio Modal states
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  const [audioModalType, setAudioModalType] = useState<'narration' | 'melody'>('melody');
  
  // Audio Selection Modal states
  const [isAudioSelectionModalOpen, setIsAudioSelectionModalOpen] = useState(false);

  // Refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const playingSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const masterGainRef = useRef<GainNode | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const timeRulerRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();
  const playStartTimeRef = useRef<number>(0);
  const playStartOffsetRef = useRef<number>(0);
  const hasInitializedRef = useRef<boolean>(false);
  const hasShownProjectLoadedRef = useRef<boolean>(false); // Track if we've already initialized

  const { showNotification } = useNotification();

  // Project title state
  const [projectTitle, setProjectTitle] = useState("Untitled Project");
  const [isEditingProjectTitle, setIsEditingProjectTitle] = useState(false);
  const [finalAudioUrl, setFinalAudioUrl] = useState<string | null>(null);
  const [originalAudioUrl, setOriginalAudioUrl] = useState<string | null>(null);
  const [projectData, setProjectData] = useState<any>(null);

  // Debug finalAudioUrl state
  useEffect(() => {
    //console.log('🎯 finalAudioUrl state changed:', finalAudioUrl);
  }, [finalAudioUrl]);

  // ✅ FIX: Update projectData whenever finalAudioUrl changes so VerticalNav sees the latest audio
  useEffect(() => {
    if (finalAudioUrl && projectData) {
      setProjectData((prev: any) => ({
        ...prev,
        finalAudio: finalAudioUrl
      }));
      console.log('✅ ProjectData updated with new finalAudio:', finalAudioUrl);
    }
  }, [finalAudioUrl]);

  // Handle updating the project title
  const handleUpdateProjectTitle = async (newTitle: string) => {
    if (!projectId || !newTitle.trim()) {
      setIsEditingProjectTitle(false);
      return;
    }
    
    try {
      const response = await fetch(getApiUrl(`/projects/${projectId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({ title: newTitle.trim() }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update project title');
      }
      
      setProjectTitle(newTitle.trim());
      setIsEditingProjectTitle(false);
      
      // showNotification({
      //   title: 'Success',
      //   message: 'Project title updated successfully',
      //   type: 'success'
      // });
    } catch (error) {
      console.error('Error updating project title:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to update project title',
        type: 'error'
      });
      setIsEditingProjectTitle(false);
    }
  };

  // Handle back navigation
  const handleBackNavigation = () => {
    navigate('/audio-generation');
  };

  // ✅ NEW: Handle track activation (lazy loading)
  const handleTrackActivation = async (trackId: string) => {
    if (activeTrackId === trackId) {
      console.log(`Track ${trackId} is already active`);
      return;
    }

    try {
      // Set loading state
      setTrackLoadStates(prev => new Map(prev.set(trackId, 'LOADING')));
      
      // Activate track on backend (deactivates others) - all tracks are backend tracks after auto-save on split
      await TrackActivationService.activateTrack(trackId);
      
      // Update frontend state
      setActiveTrackId(trackId);
      
      // Update all track states
      setTrackLoadStates(prev => {
        const newStates = new Map(prev);
        // Set all tracks to dormant
        tracks.forEach(track => {
          if (track.id !== trackId) {
            newStates.set(track.id, 'DORMANT');
          }
        });
        // Set activated track to active
        newStates.set(trackId, 'ACTIVE');
        return newStates;
      });

      // Update tracks state to reflect activation - use lazy loading for all tracks
      setTracks(prevTracks => 
        prevTracks.map(track => ({
          ...track,
          isActive: track.id === trackId,
          // Only keep audio buffer for active track (lazy loading)
          audioBuffer: track.id === trackId ? track.audioBuffer : null,
        }))
      );

      // ✅ DEBUG: Log memory state after activation
      setTimeout(() => {
        const activeTracks = tracks.filter(t => t.isActive);
        const tracksWithBuffer = tracks.filter(t => t.audioBuffer !== null);
        console.log('🧠 MEMORY DEBUG AFTER ACTIVATION:');
        console.log(`📊 Active tracks: ${activeTracks.length}`);
        console.log(`💾 Tracks with audioBuffer: ${tracksWithBuffer.length}`);
        console.log(`🎯 Active track ID: ${trackId}`);
        console.log('📋 All track states:', tracks.map(t => ({
          id: t.id.substr(0, 8),
          name: t.name,
          isActive: t.isActive,
          hasBuffer: !!t.audioBuffer,
          loadState: trackLoadStates.get(t.id)
        })));
      }, 100);

      console.log(`✅ Track ${trackId} activated successfully`);
      
    } catch (error) {
      console.error('❌ Failed to activate track:', error);
      setTrackLoadStates(prev => new Map(prev.set(trackId, 'ERROR')));
      
      showNotification({
        title: 'Error',
        message: 'Failed to activate track',
        type: 'error'
      });
    }
  };

  // ✅ DEBUG: Expose debug functions to window for console access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).debugAudioTracks = () => {
        console.log('🎵 AUDIO TRACKS DEBUG INFO');
        console.log('='.repeat(50));
        console.log(`📊 Total tracks: ${tracks.length}`);
        console.log(`🎯 Active track: ${activeTrackId || 'None'}`);
        console.log(`💾 Tracks with audioBuffer: ${tracks.filter(t => t.audioBuffer !== null).length}`);
        console.log('📋 Track Details:');
        tracks.forEach((track, i) => {
          console.log(`  ${i + 1}. ${track.name}`);
          console.log(`     ID: ${track.id}`);
          console.log(`     Active: ${track.isActive ? '✅' : '❌'}`);
          console.log(`     Has Buffer: ${track.audioBuffer ? '💾' : '🚫'}`);
          console.log(`     Load State: ${trackLoadStates.get(track.id) || 'Unknown'}`);
          console.log(`     Duration: ${track.duration}s`);
          console.log('');
        });
        
        // Memory estimation
        const activeBuffers = tracks.filter(t => t.audioBuffer !== null);
        const estimatedMemory = activeBuffers.length * 5; // ~5MB per track estimate
        console.log(`🧠 Estimated Memory Usage: ~${estimatedMemory}MB`);
        console.log('='.repeat(50));
        console.log('💡 Run this command anytime: window.debugAudioTracks()');
      };

      // Also expose track activation for testing
      (window as any).activateTrackById = (trackId: string) => {
        console.log(`🎯 Manually activating track: ${trackId}`);
        handleTrackActivation(trackId);
      };

      console.log('🛠️ DEBUG FUNCTIONS AVAILABLE:');
      console.log('  - window.debugAudioTracks() - Show memory/state info');
      console.log('  - window.activateTrackById("track-id") - Activate specific track');
    }
  }, [tracks, activeTrackId, trackLoadStates]);

  // ✅ EMERGENCY: Fix any tracks with invalid lane indexes that slip through
  useEffect(() => {
    const tracksWithInvalidLanes = tracks.filter(track => 
      track.laneIndex < 0 || track.laneIndex > 2 || track.laneIndex === undefined || track.laneIndex === null
    );
    
    if (tracksWithInvalidLanes.length > 0) {
      console.error('🚨 FOUND TRACKS WITH INVALID LANES:', tracksWithInvalidLanes.map(t => ({
        name: t.name,
        id: t.id,
        invalidLane: t.laneIndex
      })));
      
      console.log('🔧 BEFORE EMERGENCY FIX - All track lanes:', tracks.map((t, idx) => 
        `Track ${idx} (${t.name.substr(0, 15)}) → Lane ${t.laneIndex}`
      ));
      
      // Fix them immediately
      setTracks(prevTracks => prevTracks.map((track, index) => ({
        ...track,
        laneIndex: index % 3 // Force valid redistribution
      })));
      
      console.log('🔧 EMERGENCY FIX APPLIED: All tracks redistributed to valid lanes (0, 1, 2)');
    } else {
      console.log('✅ All tracks have valid lanes:', tracks.map((t, idx) => 
        `Track ${idx} (${t.name.substr(0, 15)}) → Lane ${t.laneIndex}`
      ));
    }
  }, [tracks]);

  // ✅ Auto-save after track split when tracks state has updated
  useEffect(() => {
    if (splitAutoSave) {
      const { wasOriginalTrackActive, firstTrack } = splitAutoSave;
      
      const doAutoSave = async () => {
        try {
          console.log('💾 Auto-saving project after track split (state updated)...');
          console.log('🎵 AFTER SPLIT: Total tracks =', tracks.length);
          console.log('🎵 All track names:', tracks.map(t => t.name));
          console.log('🎵 Split tracks in current state:', tracks.filter(t => t.name.includes('(Part')).map(t => ({ 
            name: t.name, 
            startTime: t.startTime, 
            duration: t.duration 
          })));
          
          await saveProject();
          console.log('✅ Project saved successfully');
          
          // Clear the trigger immediately to prevent duplicate saves
          setSplitAutoSave(null);
          
          // Reload immediately after save (no timeout needed)
          console.log('🔄 Reloading tracks from backend...');
          const reloaded = await loadProjectTracks();
          if (reloaded) {
            console.log('✅ Tracks reloaded from backend');
            
            // Find and activate the first part track if it was originally active
            if (wasOriginalTrackActive) {
              setTimeout(() => {
                const reloadedFirstTrack = tracks.find(t => 
                  t.name.includes('Part 1') && Math.abs(t.startTime - firstTrack.startTime) < 0.1
                );
                if (reloadedFirstTrack) {
                  console.log(`🎯 Setting active track to reloaded: ${reloadedFirstTrack.name}`);
                  setActiveTrackId(reloadedFirstTrack.id);
                }
              }, 500); // Small delay to let state settle
            }
          } else {
            console.error('❌ Failed to reload tracks after save');
          }
          
        } catch (error) {
          console.error('❌ Failed to auto-save after split:', error);
          // showNotification({
          //   title: 'Split Successful', 
          //   message: 'Track split successfully, but auto-save failed. Please save manually.',
          //   type: 'warning'
          // });
        }
      };
      
      doAutoSave();
    }
  }, [tracks, splitAutoSave]);

  // Load project tracks from API
  const loadProjectTracks = async (): Promise<boolean> => {
    if (!projectId) return false;
    
    setIsLoading(true);
    
    try {
      // AudioEditor should only load 'audio' type tracks, not 'sound' tracks
      const response = await fetch(getApiUrl(`/music-generation/projects/${projectId}/tracks?type=audio`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // No tracks found, this is fine for new projects
          return false;
        }
        throw new Error('Failed to load project tracks');
      }

      const result = await response.json();
      
      if (result.success && result.data.tracks && result.data.tracks.length > 0) {
        const loadedTracks = result.data.tracks.map((track: any) => ({
          id: track.id,
          name: track.name,
          url: track.s3Url, // Backend uses 's3Url', frontend uses 'url'
          audioBuffer: track.isActive ? null : null, // ✅ Will be loaded only for active tracks
          startTime: track.startTime,
          duration: track.duration,
          originalDuration: track.originalDuration,
          volume: track.volume,
          isMuted: track.isMuted,
          color: track.color,
          laneIndex: Math.max(0, Math.min(track.laneIndex ?? 0, 2)), // ✅ Force lane to be 0, 1, or 2
          fadeInDuration: track.fadeInDuration,
          fadeOutDuration: track.fadeOutDuration,
          trimStart: track.trimStart,
          trimEnd: track.trimEnd,
          isSelected: track.isSelected,
          isActive: track.isActive, // ✅ NEW: Lazy loading state from backend
          isSolo: false, // Required by interface
          pan: 0, // Required by interface
        }));

        // ✅ Redistribute tracks evenly across 3 lanes to avoid overcrowding
        const redistributedTracks = loadedTracks.map((track: any, index: number) => ({
          ...track,
          laneIndex: index % 3 // Distribute evenly: track 0→lane 0, track 1→lane 1, track 2→lane 2, track 3→lane 0, etc.
        }));

        console.log('🎯 LANE DISTRIBUTION:', redistributedTracks.map(t => ({
          name: t.name.substr(0, 20),
          originalLane: loadedTracks.find(lt => lt.id === t.id)?.laneIndex,
          newLane: t.laneIndex
        })));

        console.log('🔢 TRACK INDEX TO LANE MAPPING:', redistributedTracks.map((t, idx) => 
          `Track ${idx} (${t.name.substr(0, 15)}) → Lane ${t.laneIndex}`
        ));

        // ✅ NEW: Set up track load states and active track
        const newTrackStates = new Map<string, 'ACTIVE' | 'LOADING' | 'DORMANT' | 'ERROR'>();
        let newActiveTrackId: string | null = null;

        redistributedTracks.forEach((track: any) => {
          if (track.isActive) {
            newTrackStates.set(track.id, 'ACTIVE');
            newActiveTrackId = track.id;
          } else {
            newTrackStates.set(track.id, 'DORMANT');
          }
        });

        setTrackLoadStates(newTrackStates);
        setActiveTrackId(newActiveTrackId);

        console.log(`🎵 Loaded ${redistributedTracks.length} tracks, active track: ${newActiveTrackId || 'none'}`);

        // Check if we have any tracks at all (including invalid ones)
        const hasAnyTracks = redistributedTracks.length > 0;
        
        // Filter out tracks with blob URLs and warn the user
        const validTracks = redistributedTracks.filter((track: AudioTrack) => {
          if (track.url.startsWith('blob:')) {
            //console.warn(`Filtering out track "${track.name}" with invalid blob URL`);
            return false;
          }
          return true;
        });

        const invalidTrackCount = redistributedTracks.length - validTracks.length; // ✅ Use redistributed count
        
        setTracksWithValidLanes(validTracks); // ✅ Use safe setter that validates lane indexes
        
        // ✅ DEBUG: Verify lane distribution in state
        console.log('🎯 FINAL TRACK LANES SET:', validTracks.map(t => ({
          name: t.name.substr(0, 20),
          laneIndex: t.laneIndex,
          id: t.id.substr(0, 8)
        })));
        
        // Load editor settings from metadata
        const metadata = result.data.metadata || {};
        if (metadata.masterVolume !== undefined) setMasterVolume(metadata.masterVolume);
        if (metadata.isMasterMuted !== undefined) setIsMasterMuted(metadata.isMasterMuted);
        if (metadata.totalDuration !== undefined) setTotalDuration(metadata.totalDuration);
        if (metadata.zoomLevel !== undefined) setZoomLevel(metadata.zoomLevel);
        if (metadata.snapToGrid !== undefined) setSnapToGrid(metadata.snapToGrid);
        if (metadata.gridSize !== undefined) setGridSize(metadata.gridSize);

        if (invalidTrackCount > 0) {
          showNotification({
            title: 'Some Tracks Unavailable',
            message: `${invalidTrackCount} track(s) could not be loaded due to corrupted references. ${validTracks.length > 0 ? `Loaded ${validTracks.length} valid tracks.` : 'Loading original audio as fallback.'}`,
            type: 'warning'
          });
        } else if (validTracks.length > 0 && !hasShownProjectLoadedRef.current) {
          hasShownProjectLoadedRef.current = true;
          // showNotification({
          //   title: 'Project Loaded',
          //   message: `Loaded ${validTracks.length} audio tracks`,
          //   type: 'success'
          // });
        }
        
        // Return true if we have valid tracks, false if we need to fallback to URL loading
        return validTracks.length > 0;
      }
      
      return false; // No tracks found
    } catch (error) {
      console.error('Load error:', error);
      showNotification({
        title: 'Load Failed',
        message: 'Failed to load project tracks. Starting with empty project.',
        type: 'warning'
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  // Combined loading effect - prioritize saved tracks over URL audio
  useEffect(() => {
    const initializeProject = async () => {
      // Prevent multiple initializations for the same project
      if (!projectId || hasInitializedRef.current) return;
      
      hasInitializedRef.current = true;
      
      // First, try to load existing saved tracks
      const hasExistingTracks = await loadProjectTracks();
      
      // Determine which audio URL to use - prioritize URL parameter, then finalAudio, then originalAudioUrl
      let audioSourceUrl = audioUrl;
      if (!audioSourceUrl && finalAudioUrl) {
        audioSourceUrl = finalAudioUrl;
        //console.log('🎵 Using finalAudio from project:', finalAudioUrl);
      } else if (!audioSourceUrl && originalAudioUrl) {
        audioSourceUrl = originalAudioUrl;
        //console.log('🎵 Using originalAudioUrl from project:', originalAudioUrl);
      }
      
      // Only load initial audio from URL if no existing tracks were found and we have an audio source
      if (!hasExistingTracks && audioSourceUrl && audioSourceUrl.trim() !== '') {
        setIsLoading(true);
        try {
          //console.log('🎵 Loading audio from URL:', audioSourceUrl);
          // Fetch the audio file from S3 URL
          const response = await fetch(audioSourceUrl);
          if (!response.ok) {
            throw new Error('Failed to fetch audio file');
          }
          
          const blob = await response.blob();
          const file = new File([blob], 'initial-audio.mp3', { type: 'audio/mpeg' });
          
          // Load the audio buffer
          const audioBuffer = await loadAudioFile(file);
          
          const newTrack: AudioTrack = {
            id: `initial-track-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: `Project Audio - ${projectId}`,
            url: audioSourceUrl, // Use the determined audio source URL
            audioBuffer,
            startTime: 0,
            duration: audioBuffer.duration,
            volume: 0.8,
            isMuted: false,
            color: generateTrackColor(),
            originalDuration: audioBuffer.duration,
            fadeInDuration: 0,
            fadeOutDuration: 0,
            trimStart: 0,
            trimEnd: audioBuffer.duration,
            laneIndex: 0, // Initial track goes to lane 0
            isSelected: false,
            isSolo: false,
            pan: 0
          };

          setTracks([newTrack]);
          setTotalDuration(Math.max(60, audioBuffer.duration));
          
          // showNotification({
          //   title: 'Audio Loaded',
          //   message: 'Project audio loaded successfully',
          //   type: 'success'
          // });
        } catch (error) {
          console.error('Error loading initial audio:', error);
          showNotification({
            title: 'Load Error',
            message: 'Failed to load project audio. You can upload new audio files.',
            type: 'error'
          });
        } finally {
          setIsLoading(false);
        }
      } else if (!hasExistingTracks && !audioSourceUrl) {
        //console.log('📝 No audio source available - empty project');
        // showNotification({
        //   title: 'No Audio Found',
        //   message: 'No audio found for this project. Upload audio files to start editing.',
        //   type: 'info'
        // });
      }
    };

    initializeProject();
  }, [projectId, audioUrl, finalAudioUrl, originalAudioUrl]); // Depend on all audio sources

  // Load project title
  useEffect(() => {
    const loadProjectTitle = async () => {
      if (!projectId) return;
      
      try {
        //console.log('🔍 Loading project title for project ID:', projectId);
        
        const response = await fetch(getApiUrl(`/projects/${projectId}`), {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });
        
        //console.log('📡 Project API response status:', response.status);
        
        if (response.ok) {
          const projectData = await response.json();
          //console.log('📦 Project data received:', projectData);
          
          if (projectData && projectData.title) {
            //console.log('✅ Setting project title to:', projectData.title);
            setProjectTitle(projectData.title);
          } else {
            console.log('⚠️ No title found in project data:', projectData);
          }
        } else {
          console.log('❌ Failed to fetch project:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('❌ Error loading project title:', error);
      }
    };
    
    loadProjectTitle();
  }, [projectId, token]); // Add token as dependency

  // Reset initialization flag when projectId changes
  useEffect(() => {
    hasInitializedRef.current = false;
  }, [projectId]);

  // Fetch project details to get title
  useEffect(() => {
    if (!projectId) return;
    
    const fetchProjectDetails = async () => {
      try {
        //console.log('🔍 Fetching project details for:', projectId);
        const response = await fetch(getApiUrl(`/projects/${projectId}`), {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });
        if (response.ok) {
          const projectData = await response.json();
          //console.log('📦 Project data received:', projectData);
          
          // Store the complete project data for VerticalNav
          setProjectData(projectData);
          
          if (projectData) {
            if (projectData.title) {
              console.log('✅ Setting project title:', projectData.title);
              setProjectTitle(projectData.title);
            }

            // ✅ NEW: Set project duration if available  
            if (projectData.duration) {
              const durationParts = projectData.duration.split(':');
              let totalSeconds = 0;

              if (durationParts.length === 2) {
                // MM:SS format (e.g., "20:05" or "120:30")
                const minutes = parseInt(durationParts[0], 10);
                const seconds = parseInt(durationParts[1], 10);
                totalSeconds = minutes * 60 + seconds;
              } else if (durationParts.length === 3) {
                // H:MM:SS format (e.g., "1:20:05")  
                const hours = parseInt(durationParts[0], 10);
                const minutes = parseInt(durationParts[1], 10);
                const seconds = parseInt(durationParts[2], 10);
                totalSeconds = hours * 3600 + minutes * 60 + seconds;
              } else {
                console.log('⚠️ Invalid duration format:', projectData.duration);
              }
              
              if (!isNaN(totalSeconds) && totalSeconds > 0) {
                console.log(`🕐 Setting project duration: ${projectData.duration} (${totalSeconds}s)`);
                setTotalDuration(totalSeconds);
              } else {
                console.log('⚠️ Invalid duration calculation:', totalSeconds);
              }
            } else {
              console.log('⚠️ No duration found in project data');
            }

            if (projectData.finalAudio) {
              //console.log('✅ Setting finalAudio URL:', projectData.finalAudio);
              setFinalAudioUrl(projectData.finalAudio);
            } else {
              console.log('❌ No finalAudio found in project data');
            }
            if (projectData.originalAudioUrl) {
              //console.log('✅ Setting originalAudio URL:', projectData.originalAudioUrl);
              setOriginalAudioUrl(projectData.originalAudioUrl);
            } else {
              console.log('❌ No originalAudioUrl found in project data');
            }
          }
        } else {
          console.log('❌ Failed to fetch project data:', response.status);
        }
      } catch (error) {
        console.error('❌ Error fetching project details:', error);
      }
    };
    
    fetchProjectDetails();
  }, [projectId]);

  // Initialize audio context
  useEffect(() => {
    const initAudio = async () => {
      try {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        masterGainRef.current = audioContextRef.current.createGain();
        masterGainRef.current.connect(audioContextRef.current.destination);
        masterGainRef.current.gain.value = masterVolume;
      } catch (error) {
        console.error('Failed to initialize audio context:', error);
      }
    };
    
    initAudio();
    
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Auto-save removed - users must manually save their projects

  // Update master volume
  useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = isMasterMuted ? 0 : masterVolume;
    }
  }, [masterVolume, isMasterMuted]);

  // Animation loop for playhead
  useEffect(() => {
    if (isPlaying && audioContextRef.current) {
      let lastTime = currentTime;
      
      const updateTime = async () => {
        const elapsed = (audioContextRef.current!.currentTime - playStartTimeRef.current);
        const newTime = playStartOffsetRef.current + elapsed;
        
        if (newTime >= totalDuration) {
          setIsPlaying(false);
          setCurrentTime(0);
          // Stop all sources
          playingSourcesRef.current.forEach(source => {
            try {
              source.stop();
            } catch (e) {
              // Source might already be stopped
            }
          });
          playingSourcesRef.current = [];
        } else {
          setCurrentTime(newTime);
          
          // Check if any new tracks should start playing
          for (const track of tracks) {
            if (!track.isMuted) {
              const trackStartTime = track.startTime;
              const trackEndTime = track.startTime + track.duration;
              
              // Check if this track just started (playhead crossed the start time)
              if (lastTime < trackStartTime && newTime >= trackStartTime && newTime < trackEndTime) {
                //console.log(`Starting track: ${track.name} at ${newTime.toFixed(2)}s`);
                
                // Load audio buffer if not already loaded
                const audioBuffer = await loadAudioBufferForTrack(track);
                
                if (audioBuffer) {
                  const source = audioContextRef.current!.createBufferSource();
                  const trackGainNode = audioContextRef.current!.createGain();
                  
                  source.buffer = audioBuffer;
                  source.connect(trackGainNode);
                  trackGainNode.connect(masterGainRef.current!);
                  
                  // Calculate timing first
                  const when = audioContextRef.current!.currentTime;
                  
                  // Set track volume with fade curves
                  const baseVolume = track.volume;
                  trackGainNode.gain.value = baseVolume;
                  
                  // Apply fade curves if configured
                  if (track.fadeInDuration > 0 || track.fadeOutDuration > 0) {
                    const trackRelativeTime = Math.max(0, newTime - trackStartTime);
                    const currentVolume = calculateVolumeWithFades(
                      trackRelativeTime, 
                      track.duration, 
                      baseVolume, 
                      track.fadeInDuration, 
                      track.fadeOutDuration
                    );
                    trackGainNode.gain.value = currentVolume;
                    
                    // Schedule fade automation for the remaining duration
                    scheduleFadeAutomation(trackGainNode, trackRelativeTime, track, when);
                  }
                  
                  // Calculate start offset within the track
                  const offset = Math.max(0, newTime - trackStartTime + track.trimStart);
                  
                  try {
                    source.start(when, offset);
                    playingSourcesRef.current.push(source);
                    
                    // Stop the source when track ends
                    const remainingDuration = track.duration - (newTime - trackStartTime);
                    source.stop(when + remainingDuration);
                    
                    //console.log(`✅ Started track: ${track.name}, remaining duration: ${remainingDuration.toFixed(2)}s`);
                  } catch (e) {
                    console.error('Error starting delayed audio source:', e);
                  }
                }
              }
            }
          }
          
          lastTime = newTime;
          animationFrameRef.current = requestAnimationFrame(updateTime);
        }
      };
      animationFrameRef.current = requestAnimationFrame(updateTime);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, totalDuration, tracks]);

  // Effect to handle navigation to content creation after audio generation
  useEffect(() => {
    if (shouldNavigateToContentCreation && finalAudioUrl && !showAudioMixer) {
      //console.log('🎯 Audio generation complete, navigating to content creation...');
      
      // Reset the flag
      setShouldNavigateToContentCreation(false);
      
      // Navigate to content creation with the new final audio
      const contentCreationUrl = `/caption?project-id=${projectId}&audio-url=${encodeURIComponent(finalAudioUrl)}`;
      
      // showNotification({
      //   title: 'Audio Generated Successfully!',
      //   message: 'Proceeding to content creation with your generated audio',
      //   type: 'success'
      // });
      
      navigate(contentCreationUrl);
    }
  }, [shouldNavigateToContentCreation, finalAudioUrl, showAudioMixer, projectId, navigate, showNotification]);

  // Timeline width calculation based on zoom
  const timelineWidth = totalDuration * pixelsPerSecond * zoomLevel;

  // Smart time marker calculation based on zoom level
  const getTimeMarkerInterval = (zoomLevel: number): number => {
    if (zoomLevel >= 4) return 0.1;      // Every 0.1 seconds when very zoomed in
    if (zoomLevel >= 2) return 0.5;      // Every 0.5 seconds when zoomed in  
    if (zoomLevel >= 1) return 1;        // Every 1 second at normal zoom
    if (zoomLevel >= 0.5) return 5;      // Every 5 seconds when zoomed out
    if (zoomLevel >= 0.25) return 10;    // Every 10 seconds when more zoomed out
    return 30;                           // Every 30 seconds when very zoomed out
  };

  const formatTimeMarker = (seconds: number, interval: number): string => {
    if (interval < 1) {
      // For sub-second intervals, show decimal places
      return seconds.toFixed(1) + 's';
    } else if (interval < 60) {
      // For second intervals, show whole seconds
      return Math.round(seconds) + 's';
    } else {
      // For minute intervals, show minutes:seconds
      const mins = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  };

  // Update snapToGridValue to use current state
  const snapToGrid_Current = (value: number): number => {
    return snapToGridValue(value, gridSize, snapToGrid);
  };

  // Scroll synchronization
  const handleTimelineScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (timeRulerRef.current && event.target !== timeRulerRef.current) {
      timeRulerRef.current.scrollLeft = (event.target as HTMLDivElement).scrollLeft;
    }
  };

  const handleRulerScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (timelineContainerRef.current && event.target !== timelineContainerRef.current) {
      timelineContainerRef.current.scrollLeft = (event.target as HTMLDivElement).scrollLeft;
    }
  };

  // Auto-center playhead effect
  useEffect(() => {
    if (!autoCenter || !timelineContainerRef.current) return;

    const container = timelineContainerRef.current;
    const playheadPositionPx = currentTime * pixelsPerSecond * zoomLevel;
    const viewportWidth = container.clientWidth;
    const currentScrollLeft = container.scrollLeft;
    
    // Calculate where the playhead is relative to the viewport
    const playheadRelativePosition = playheadPositionPx - currentScrollLeft;
    
    // Only scroll if playhead is getting close to edges (within 20% of viewport width from edge)
    const edgeThreshold = viewportWidth * 0.2;
    const shouldScroll = playheadRelativePosition < edgeThreshold || 
                        playheadRelativePosition > (viewportWidth - edgeThreshold);
    
    if (!shouldScroll && isPlaying) return; // Don't scroll if playhead is still in center area during playback
    
    const targetScrollLeft = Math.max(0, playheadPositionPx - viewportWidth / 2);

    // Use instant scroll to avoid animation conflicts
    container.scrollTo({
      left: targetScrollLeft,
      behavior: 'auto'
    });
  }, [currentTime, autoCenter, zoomLevel, pixelsPerSecond, isPlaying]);

  // Generate random color for new tracks
  const generateTrackColor = () => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    const usedColors = tracks.map(track => track.color);
    const availableColors = colors.filter(color => !usedColors.includes(color));
    return availableColors.length > 0 
      ? availableColors[0] 
      : colors[Math.floor(Math.random() * colors.length)];
  };

  // ✅ UPDATED: Load audio buffer for a track if not already loaded (respects lazy loading)
  const loadAudioBufferForTrack = async (track: AudioTrack): Promise<AudioBuffer | null> => {
    // ✅ NEW: Only load audio buffer for active tracks
    if (!track.isActive) {
      console.log(`⏸️ Skipping audio buffer load for dormant track: ${track.name}`);
      return null;
    }

    if (track.audioBuffer) {
      return track.audioBuffer; // Already loaded
    }

    if (!audioContextRef.current) {
      console.error('Audio context not initialized');
      return null;
    }

    // Check if the URL is a blob URL (which won't work after reload)
    if (track.url.startsWith('blob:')) {
      console.warn(`Track "${track.name}" has invalid blob URL. Cannot load audio buffer.`);
      showNotification({
        title: 'Audio Loading Issue',
        message: `Track "${track.name}" has an invalid reference. Please re-upload the audio file.`,
        type: 'warning'
      });
      return null;
    }

    try {
      console.log(`🎵 Loading audio buffer for ACTIVE track: ${track.name}`);
      // Use the audio proxy utility to handle CORS issues
      const audioBuffer = await loadAuthenticatedAudioBuffer(track.url, audioContextRef.current);
      
      // Update the track with the loaded audio buffer
      setTracks(prev => prev.map(t => 
        t.id === track.id ? { ...t, audioBuffer } : t
      ));

      //console.log('✅ Successfully loaded audio buffer for track:', track.name);
      return audioBuffer;
    } catch (error) {
      console.error('Error loading audio buffer for track:', track.name, error);
      showNotification({
        title: 'Audio Load Failed',
        message: `Could not load audio for "${track.name}". The file may have been moved or deleted.`,
        type: 'error'
      });
      return null;
    }
  };

  // Load audio file from File object (for uploads)
  const loadAudioFile = async (file: File): Promise<AudioBuffer> => {
    if (!audioContextRef.current) {
      throw new Error('Audio context not initialized');
    }

    const arrayBuffer = await file.arrayBuffer();
    return await audioContextRef.current.decodeAudioData(arrayBuffer);
  };

  // Handle file upload
  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Upload audio file to S3 and return the URL
  const uploadAudioFileToS3 = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('projectId', projectId || '');
    
    const response = await fetch(getApiUrl('/music-generation/upload-audio'), {
      method: 'POST',
      headers: {
        // 🔒 ADD AUTHENTICATION HEADER
        ...(token && { 'Authorization': `Bearer ${token}` }),
        // Note: Don't set Content-Type for FormData, let browser set it with boundary
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to upload audio to S3');
    }

    const result = await response.json();
    return result.data.s3Url;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setIsLoading(true);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith('audio/')) {
          showNotification({
            title: 'Invalid File',
            message: `${file.name} is not an audio file`,
            type: 'error'
          });
          continue;
        }

        // Upload to S3 first
        const s3Url = await uploadAudioFileToS3(file);
        
        // Then load the audio buffer for local playback
        const audioBuffer = await loadAudioFile(file);
        
        // Find available lane and calculate start time to avoid overlap
        const usedLanes = tracks.map(t => t.laneIndex);
        const availableLane = [0, 1, 2].find(lane => !usedLanes.includes(lane)) ?? 0; // ✅ Always force to valid lane (0-2)
        
        // Calculate start time to avoid overlap with existing tracks in the same lane
        const tracksInLane = tracks.filter(t => t.laneIndex === availableLane);
        const maxEndTime = tracksInLane.length > 0 
          ? Math.max(...tracksInLane.map(t => t.startTime + t.duration))
          : 0;
        const newStartTime = maxEndTime > 0 ? maxEndTime + 0.5 : 0; // Add 0.5 second gap
        
        const newTrack: AudioTrack = {
          id: `track-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
          url: s3Url, // Use S3 URL instead of blob URL
          audioBuffer,
          startTime: newStartTime,
          duration: audioBuffer.duration,
          volume: 0.8,
          isMuted: false,
          color: generateTrackColor(),
          originalDuration: audioBuffer.duration,
          fadeInDuration: 0,
          fadeOutDuration: 0,
          trimStart: 0,
          trimEnd: audioBuffer.duration,
          laneIndex: availableLane,
          isSelected: false,
          isSolo: false,
          pan: 0
        };

        setTracks(prev => [...prev, newTrack]);
        setTotalDuration(prev => Math.max(prev, newStartTime + audioBuffer.duration));
      }

      // showNotification({
      //   title: 'Success',
      //   message: `${files.length} audio file(s) uploaded and loaded successfully`,
      //   type: 'success'
      // });
    } catch (error) {
      console.error('Error uploading audio files:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to upload audio files. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Play/pause functionality - Enhanced version with buffer loading
  const togglePlayback = useCallback(async () => {
    if (!audioContextRef.current || !masterGainRef.current) return;

    if (isPlaying) {
      // Stop playback
      playingSourcesRef.current.forEach(source => {
        try {
          source.stop();
        } catch (e) {
          // Source might already be stopped
        }
      });
      playingSourcesRef.current = [];
      setIsPlaying(false);
    } else {
      // Start playback
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      playingSourcesRef.current = [];
      
      // Load audio buffers for tracks that need them and start playback
      for (const track of tracks) {
        if (!track.isMuted) {
          // Load audio buffer if not already loaded
          const audioBuffer = await loadAudioBufferForTrack(track);
          
          if (audioBuffer) {
            const trackStartTime = track.startTime;
            const trackEndTime = track.startTime + track.duration;
            
            // Check if track should be playing at current time
            if (currentTime >= trackStartTime && currentTime < trackEndTime) {
              const source = audioContextRef.current!.createBufferSource();
              const trackGainNode = audioContextRef.current!.createGain();
              
              source.buffer = audioBuffer;
              source.connect(trackGainNode);
              trackGainNode.connect(masterGainRef.current!);
              
              // Calculate timing first
              const when = audioContextRef.current!.currentTime;
              
              // Set track volume with fade curves
              const baseVolume = track.volume;
              trackGainNode.gain.value = baseVolume;
              
              // Apply fade curves if configured
              if (track.fadeInDuration > 0 || track.fadeOutDuration > 0) {
                const trackRelativeTime = Math.max(0, currentTime - trackStartTime);
                const currentVolume = calculateVolumeWithFades(
                  trackRelativeTime, 
                  track.duration, 
                  baseVolume, 
                  track.fadeInDuration, 
                  track.fadeOutDuration
                );
                trackGainNode.gain.value = currentVolume;
                
                // Schedule fade automation for the remaining duration
                scheduleFadeAutomation(trackGainNode, trackRelativeTime, track, when);
              }
              
              // Calculate start offset within the track
              const offset = Math.max(0, currentTime - trackStartTime + track.trimStart);
              
              try {
                source.start(when, offset);
                playingSourcesRef.current.push(source);
                
                // Stop the source when track ends
                const remainingDuration = track.duration - (currentTime - trackStartTime);
                source.stop(when + remainingDuration);
              } catch (e) {
                console.error('Error starting audio source:', e);
              }
            }
          }
        }
      }
      
      playStartTimeRef.current = audioContextRef.current.currentTime;
      playStartOffsetRef.current = currentTime;
      setIsPlaying(true);
    }
  }, [isPlaying, currentTime, tracks]);

  // Seek to specific time
  const seekTo = (time: number) => {
    const clampedTime = Math.max(0, Math.min(time, totalDuration));
    setCurrentTime(clampedTime);
    
    if (isPlaying) {
      // Restart playback from new position
      togglePlayback();
      setTimeout(() => togglePlayback(), 100);
    }
  };

  // Handle timeline click - Fixed for proper zoom behavior
  const handleTimelineClick = (event: React.MouseEvent) => {
    if (!timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    // Calculate time based on actual timeline scaling
    const clickTime = (clickX / pixelsPerSecond) / zoomLevel;
    
    const newTime = snapToGrid_Current(Math.max(0, Math.min(clickTime, totalDuration)));
    seekTo(newTime);
  };

  // Zoom functions - Fixed to only affect timeline scale
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 10));
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.1));
  };

  const resetZoom = () => {
    setZoomLevel(1);
  };

  // Track manipulation functions
  const selectTrack = (trackId: string) => {
    setTracks(prev => prev.map(track => ({
      ...track,
      isSelected: track.id === trackId
    })));
    setSelectedTrackId(trackId);
  };

  const deleteTrack = (trackId: string) => {
    setTracks(prev => prev.filter(track => track.id !== trackId));
    if (selectedTrackId === trackId) {
      setSelectedTrackId(null);
    }
  };

  const duplicateTrack = (trackId: string) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;

    // Find an available lane or use lane 0 as fallback
    const usedLanes = tracks.map(t => t.laneIndex);
    const availableLane = [0, 1, 2].find(lane => !usedLanes.includes(lane)) ?? Math.max(0, Math.min(track.laneIndex, 2)); // ✅ Constrain to 0-2

    const newTrack: AudioTrack = {
      ...track,
      id: `track-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: `${track.name} (Copy)`,
      startTime: snapToGrid_Current(track.startTime + track.duration),
      color: generateTrackColor(),
      laneIndex: availableLane,
      isSelected: false
    };

    setTracks(prev => [...prev, newTrack]);
    // Extend timeline to accommodate the duplicated track
    setTotalDuration(prev => Math.max(prev, newTrack.startTime + newTrack.duration));
  };

  // Fill gaps between audio tracks
  const handleFillGaps = () => {
    if (tracks.length < 2) return;
    
    const filledTracks = fillAudioGaps(tracks);
    setTracks(filledTracks);
    
    // Update total duration based on new track positions
    const maxEndTime = Math.max(...filledTracks.map(t => t.startTime + t.duration));
    setTotalDuration(maxEndTime);
    
    // showNotification({
    //   title: 'Success',
    //   message: 'Gaps filled successfully!',
    //   type: 'success'
    // });
  };

  const handleSoundEffectAdd = async (soundEffectUrl: string, filename: string, duration: number) => {
    try {
      // Load the audio buffer from the S3 URL
      const response = await fetch(soundEffectUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch sound effect');
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContextRef.current!.decodeAudioData(arrayBuffer);
      
      // Find available position at current playhead
      let targetLane = 0;
      let targetStartTime = currentTime;
      
      // Check if there's already a track at current time in each lane
      for (let lane = 0; lane <= 2; lane++) {
        const tracksInLane = tracks.filter(t => t.laneIndex === lane);
        const hasConflict = tracksInLane.some(track => 
          currentTime >= track.startTime && currentTime < (track.startTime + track.duration)
        );
        
        if (!hasConflict) {
          targetLane = lane;
          break;
        }
      }
      
      // If all lanes are occupied at current time, add at the end of timeline
      if (targetLane === 2) {
        const allTracks = tracks.filter(t => t.laneIndex === 2);
        const hasConflict = allTracks.some(track => 
          currentTime >= track.startTime && currentTime < (track.startTime + track.duration)
        );
        
        if (hasConflict) {
          // Find the end of the timeline
          const maxEndTime = Math.max(...tracks.map(t => t.startTime + t.duration), currentTime);
          targetStartTime = maxEndTime + 0.5; // Add small gap
          targetLane = 0; // Use first lane for end placement
        }
      }
      
      const newTrack: AudioTrack = {
        id: `sound-effect-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: filename.replace(/\.[^/.]+$/, '') || 'Sound Effect', // Remove extension
        url: soundEffectUrl,
        audioBuffer,
        startTime: snapToGrid_Current(targetStartTime),
        duration: audioBuffer.duration,
        volume: 0.8,
        isMuted: false,
        color: generateTrackColor(),
        originalDuration: audioBuffer.duration,
        fadeInDuration: 0,
        fadeOutDuration: 0,
        trimStart: 0,
        trimEnd: audioBuffer.duration,
        laneIndex: targetLane,
        isSelected: false,
        isSolo: false,
        pan: 0
      };

      setTracks(prev => [...prev, newTrack]);
      setTotalDuration(prev => Math.max(prev, targetStartTime + audioBuffer.duration));
      
      // showNotification({
      //   title: 'Sound Effect Added',
      //   message: `"${newTrack.name}" added to timeline at ${targetStartTime.toFixed(1)}s`,
      //   type: 'success'
      // });
      
    } catch (error) {
      console.error('Error adding sound effect:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to add sound effect to timeline',
        type: 'error'
      });
    }
  };

  const handleOpenSoundEffectsModal = () => {
    setShowSoundEffectsModal(true);
  };

  const handleSoundEffectGenerated = (soundEffectUrl: string, filename: string, duration: number) => {
    // Add to timeline
    handleSoundEffectAdd(soundEffectUrl, filename, duration);
    // Refresh the sound effects list
    setSoundEffectsRefreshTrigger(prev => prev + 1);
  };

  // Handle opening AI Audio modal
  const handleOpenAIAudioModal = (type: 'narration' | 'melody' = 'melody') => {
    setAudioModalType(type);
    setIsAudioModalOpen(true);
  };

  // Handle AI audio generation
  const handleAudioGenerated = (audioUrl: string, projectId: string, type: 'narration' | 'melody') => {
    //console.log('AI Audio generated:', { audioUrl, projectId, type });
    
    // Show success notification
    // showNotification({
    //   title: 'Success',
    //   message: `${type === 'narration' ? 'Narration' : 'Melody'} generated successfully! Adding to timeline...`,
    //   type: 'success'
    // });
    
    // Close modal
    setIsAudioModalOpen(false);
    
    // Add the generated audio to the timeline
    handleAudioGeneratedToTimeline(audioUrl, type);
  };

  // Add generated audio to timeline
  const handleAudioGeneratedToTimeline = async (audioUrl: string, type: 'narration' | 'melody') => {
    try {
      // Fetch the audio file from the S3 URL
      const response = await fetch(audioUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch generated audio');
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContextRef.current!.decodeAudioData(arrayBuffer);
      
      // Find available position
      let targetLane = 0;
      let targetStartTime = currentTime;
      
      // Check if there's already a track at current time in each lane
      for (let lane = 0; lane <= 2; lane++) {
        const tracksInLane = tracks.filter(t => t.laneIndex === lane);
        const hasConflict = tracksInLane.some(track => 
          currentTime >= track.startTime && currentTime < (track.startTime + track.duration)
        );
        
        if (!hasConflict) {
          targetLane = lane;
          break;
        }
      }
      
      // If all lanes are occupied at current time, add at the end of timeline
      if (targetLane === 2) {
        const allTracks = tracks.filter(t => t.laneIndex === 2);
        const hasConflict = allTracks.some(track => 
          currentTime >= track.startTime && currentTime < (track.startTime + track.duration)
        );
        
        if (hasConflict) {
          // Find the end of the timeline
          const maxEndTime = Math.max(...tracks.map(t => t.startTime + t.duration), currentTime);
          targetStartTime = maxEndTime + 0.5; // Add small gap
          targetLane = 0; // Use first lane for end placement
        }
      }
      
      const newTrack: AudioTrack = {
        id: `ai-generated-${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: `AI ${type === 'narration' ? 'Narration' : 'Melody'} - ${new Date().toLocaleTimeString()}`,
        url: audioUrl,
        audioBuffer,
        startTime: snapToGrid_Current(targetStartTime),
        duration: audioBuffer.duration,
        volume: 0.8,
        isMuted: false,
        color: generateTrackColor(),
        originalDuration: audioBuffer.duration,
        fadeInDuration: 0,
        fadeOutDuration: 0,
        trimStart: 0,
        trimEnd: audioBuffer.duration,
        laneIndex: targetLane,
        isSelected: false,
        isSolo: false,
        pan: 0
      };

      setTracks(prev => [...prev, newTrack]);
      setTotalDuration(prev => Math.max(prev, targetStartTime + audioBuffer.duration));
      
      // showNotification({
      //   title: `${type === 'narration' ? 'Narration' : 'Melody'} Added`,
      //   message: `AI-generated ${type} added to timeline at ${targetStartTime.toFixed(1)}s`,
      //   type: 'success'
      // });
      
    } catch (error) {
      console.error('Error adding AI audio to timeline:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to add generated audio to timeline',
        type: 'error'
      });
    }
  };

  const updateTrackVolume = (trackId: string, volume: number) => {
    setTracks(prev => prev.map(track => 
      track.id === trackId ? { ...track, volume } : track
    ));
  };

  const toggleTrackMute = (trackId: string) => {
    setTracks(prev => prev.map(track => {
      if (track.id === trackId) {
        if (track.isMuted) {
          // Unmuting: restore volume to 0.8 (80%) or previous value
          return { 
            ...track, 
            isMuted: false,
            volume: track.volume === 0 ? 0.8 : track.volume 
          };
        } else {
          // Muting: set volume to 0
          return { 
            ...track, 
            isMuted: true,
            volume: 0 
          };
        }
      }
      return track;
    }));
  };

  const splitTrack = async (trackId: string, splitTime?: number) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;

    // Load audio buffer if not already loaded
    const audioBuffer = await loadAudioBufferForTrack(track);
    if (!audioBuffer) {
      showNotification({
        title: 'Split Failed',
        message: 'Could not load audio data for splitting',
        type: 'error'
      });
      return;
    }

    const splitAt = splitTime ?? currentTime;
    const relativeTime = splitAt - track.startTime;
    if (relativeTime <= 0 || relativeTime >= track.duration) return;

    // Create two new tracks
    const wasOriginalTrackActive = track.isActive;
    
    const firstTrack: AudioTrack = {
      ...track,
      audioBuffer, // Both parts get full audio buffer until saved to backend
      id: `track-${Date.now()}-1-${Math.random().toString(36).substr(2, 9)}`,
      duration: relativeTime,
      trimEnd: track.trimStart + relativeTime,
      name: `${track.name} (Part 1)`,
      isSelected: false,
      isActive: wasOriginalTrackActive // ✅ Keep original activation state
    };

    const secondTrack: AudioTrack = {
      ...track,
      audioBuffer, // ✅ Both parts get audio buffer - no lazy loading for local tracks
      id: `track-${Date.now()}-2-${Math.random().toString(36).substr(2, 9)}`,
      startTime: snapToGrid_Current(splitAt),
      duration: track.duration - relativeTime,
      trimStart: track.trimStart + relativeTime,
      name: `${track.name} (Part 2)`,
      color: generateTrackColor(),
      isSelected: false,
      isActive: false // ✅ Second track is dormant but has audioBuffer
    };

    console.log(`📊 BEFORE SPLIT: Total tracks = ${tracks.length}`);
    console.log(`🎯 Splitting track: ${track.name} (ID: ${track.id})`);
    
    setTracksWithValidLanes(prev => {
      const filtered = prev.filter(t => t.id !== trackId);
      const result = filtered.concat([firstTrack, secondTrack]);
      console.log(`📊 DURING SPLIT: ${prev.length} → filter(${filtered.length}) → add 2 → ${result.length}`);
      return result;
    });

    // ✅ Store split info for auto-save
    const splitInfo = { wasOriginalTrackActive, firstTrack };
    setSplitAutoSave(splitInfo);
  };

  // Resize handle functionality for trimming tracks
  const handleResizeMouseDown = (trackId: string, side: 'left' | 'right', event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setDraggedTrack(trackId);
    setResizeMode(side);
    setIsDragging(true);
    
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!timelineContainerRef.current) return;
      
      const containerRect = timelineContainerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left + timelineContainerRef.current.scrollLeft;
      const mouseTime = mouseX / (pixelsPerSecond * zoomLevel);
      
      setTracks(prev => prev.map(currentTrack => {
        if (currentTrack.id !== trackId) return currentTrack;
        
        if (side === 'left') {
          // Resize from left (trim start)
          const newStartTime = Math.max(0, Math.min(mouseTime, currentTrack.startTime + currentTrack.duration - 0.1));
          const newDuration = (currentTrack.startTime + currentTrack.duration) - newStartTime;
          const newTrimStart = currentTrack.trimStart + (newStartTime - currentTrack.startTime);
          
          return {
            ...currentTrack,
            startTime: snapToGrid_Current(newStartTime),
            duration: newDuration,
            trimStart: Math.max(0, Math.min(newTrimStart, currentTrack.originalDuration - 0.1))
          };
        } else {
          // Resize from right (trim end)
          const maxEndTime = currentTrack.startTime + (currentTrack.originalDuration - currentTrack.trimStart);
          const newEndTime = Math.max(currentTrack.startTime + 0.1, Math.min(mouseTime, maxEndTime));
          const newDuration = newEndTime - currentTrack.startTime;
          
          return {
            ...currentTrack,
            duration: newDuration,
            trimEnd: currentTrack.trimStart + newDuration
          };
        }
      }));
    };
    
    const handleMouseUp = () => {
      setDraggedTrack(null);
      setResizeMode(null);
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  const handleTrackMouseDown = (trackId: string, event: React.MouseEvent) => {
    if (event.button !== 0) return; // Only left mouse button
    
    const startX = event.clientX;
    const startY = event.clientY;
    let hasMoved = false;
    
    // Get the track's current position when drag starts
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    // Calculate the offset from where the user clicked within the track
    const trackElement = event.currentTarget as HTMLElement;
    const trackRect = trackElement.getBoundingClientRect();
    const clickOffsetX = event.clientX - trackRect.left;
    
    const handleMouseMove = (e: MouseEvent) => {
      const moveDistance = Math.abs(e.clientX - startX) + Math.abs(e.clientY - startY);
      
      // Only start dragging if moved more than 5 pixels
      if (moveDistance > 5 && !hasMoved) {
        hasMoved = true;
        setDraggedTrack(trackId);
        setIsDragging(true);
      }
      
      if (hasMoved && timelineContainerRef.current) {
        const containerRect = timelineContainerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - containerRect.left + timelineContainerRef.current.scrollLeft;
        const mouseY = e.clientY - containerRect.top;
        
        // Calculate new position accounting for where the user grabbed the track
        const grabOffsetInTime = clickOffsetX / (pixelsPerSecond * zoomLevel);
        const newStartTime = Math.max(0, (mouseX / (pixelsPerSecond * zoomLevel)) - grabOffsetInTime);
        const newLane = Math.max(0, Math.min(2, Math.floor(mouseY / 90)));
        
        // Update track position immediately
        setTracks(prev => prev.map(currentTrack => 
          currentTrack.id === trackId 
            ? { 
                ...currentTrack, 
                startTime: snapToGrid_Current(newStartTime),
                laneIndex: newLane
              }
            : currentTrack
        ));
      }
    };
    
    const handleMouseUp = (e: MouseEvent) => {
      // If we didn't move much, treat it as a click
      if (!hasMoved) {
        selectTrack(trackId);
      } else {
        // If track was moved, calculate timeline extension from final position
        if (timelineContainerRef.current) {
          const containerRect = timelineContainerRef.current.getBoundingClientRect();
          const mouseX = e.clientX - containerRect.left + timelineContainerRef.current.scrollLeft;
          const grabOffsetInTime = clickOffsetX / (pixelsPerSecond * zoomLevel);
          const finalStartTime = Math.max(0, (mouseX / (pixelsPerSecond * zoomLevel)) - grabOffsetInTime);
          const snappedStartTime = snapToGrid_Current(finalStartTime);
          
          // Get the original track to know its duration
          if (track) {
            const trackEndTime = snappedStartTime + track.duration;
            setTotalDuration(prev => Math.max(prev, trackEndTime));
          }
        }
      }
      
      setDraggedTrack(null);
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Save project functionality - Updated to use API
  const saveProject = async () => {
    if (tracks.length === 0) {
      showNotification({
        title: 'Nothing to Save',
        message: 'Add some audio tracks before saving',
        type: 'error'
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const projectData = {
        tracks: tracks.map(track => ({
          id: track.id, // ✅ Include track ID to preserve during updates
          name: track.name,
          s3Url: track.url, // Frontend uses 'url', backend expects 's3Url'
          startTime: track.startTime,
          duration: track.duration,
          originalDuration: track.originalDuration,
          volume: track.volume,
          isMuted: track.isMuted,
          color: track.color,
          laneIndex: track.laneIndex,
          fadeInDuration: track.fadeInDuration,
          fadeOutDuration: track.fadeOutDuration,
          trimStart: track.trimStart,
          trimEnd: track.trimEnd,
          isSelected: track.isSelected,
          trackType: 'audio' 
        })),
        masterVolume: masterVolume,
        isMasterMuted: isMasterMuted,
        totalDuration: totalDuration,
        zoomLevel: zoomLevel,
        snapToGrid: snapToGrid,
        gridSize: gridSize
      };

      // Save to backend - Updated API URL
      const response = await fetch(getApiUrl(`/music-generation/projects/${projectId}/tracks`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(projectData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save project');
      }

      const result = await response.json();
      
      // ✅ Handle ID mapping from backend to update frontend track IDs
      if (result.data?.frontendIdMapping) {
        console.log('🔄 Updating frontend track IDs with backend mapping:', result.data.frontendIdMapping);
        
        setTracks(prevTracks => prevTracks.map(track => {
          const backendId = result.data.frontendIdMapping[track.id];
          if (backendId) {
            console.log(`🆔 Mapping frontend ID ${track.id} → backend ID ${backendId}`);
            return { ...track, id: backendId };
          }
          return track;
        }));

        // Update activeTrackId if it was mapped
        if (activeTrackId && result.data.frontendIdMapping[activeTrackId]) {
          const newActiveId = result.data.frontendIdMapping[activeTrackId];
          console.log(`🎯 Updating active track ID: ${activeTrackId} → ${newActiveId}`);
          setActiveTrackId(newActiveId);
        }
      }
      
      // showNotification({
      //   title: 'Project Saved',
      //   message: `Successfully saved ${result.data.tracks?.length || 0} audio tracks`,
      //   type: 'success'
      // });
    } catch (error) {
      console.error('Save error:', error);
      showNotification({
        title: 'Save Failed',
        message: error.message || 'Failed to save project. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Export/download functionality
  const exportAudio = async () => {
    if (!finalAudioUrl) {
      showNotification({
        title: 'No Final Audio',
        message: 'Please create a mixed audio first using the Mix Audio feature',
        type: 'error'
      });
      return;
    }

    setIsLoading(true);

    try {
      // showNotification({
      //   title: 'Download Starting',
      //   message: 'Preparing your final mixed audio for download...',
      //   type: 'info'
      // });

      // Fetch the audio file as a blob to ensure proper download
      const response = await fetch(finalAudioUrl, {
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch audio file: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = url;
      link.download = `${projectTitle.replace(/[^a-zA-Z0-9]/g, '_')}_final_mix.wav`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // showNotification({
      //   title: 'Download Complete',
      //   message: 'Your final mixed audio has been downloaded successfully',
      //   type: 'success'
      // });
    } catch (error) {
      console.error('Download error:', error);
      
      // Fallback: try opening in new tab
      try {
        window.open(finalAudioUrl, '_blank');
        // showNotification({
        //   title: 'Download Alternative',
        //   message: 'Opening file in new tab. You can save it from there.',
        //   type: 'info'
        // });
      } catch (fallbackError) {
        showNotification({
          title: 'Download Failed',
          message: 'Failed to download the mixed audio. Please try again or contact support.',
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selectedTrack = tracks.find(track => track.id === selectedTrackId);

  // Handle vertical navigation
  const handleNavSelected = (key: StepIndicatorNames) => {
    //console.log('🔄 Audio Editor navigation:', key, 'Project ID:', projectId);
    
    // Handle navigation based on the selected step
    switch (key) {
      case StepIndicatorNames.ScriptGeneration:
        // Navigate to script generator for this project
        if (projectId) {
          //console.log('✅ Navigating to script generator for project:', projectId);
          navigate(`/script-generator/${projectId}`);
        } else {
          console.log('⚠️ No project ID, navigating to script landing');
          navigate('/script-generator');
        }
        break;
      case StepIndicatorNames.IdeaCreation:
        // Navigate to prompt generator for this project
        if (projectId) {
          //console.log('✅ Navigating to prompt generator for project:', projectId);
          navigate(`/prompt-generator/${projectId}`);
        } else {
          //console.log('⚠️ No project ID, navigating to general prompt generator');
          navigate('/prompt-generator');
        }
        break;
      case StepIndicatorNames.AudioGeneration:
        // Already in audio editor - no action needed
        //console.log('ℹ️ Already in audio editor - staying here');
        break;
      case StepIndicatorNames.ContentCreation:
        // Check if we have audio data - navigate directly if available, show modal if not
        if (finalAudioUrl || audioUrl) {
          //console.log('✅ Audio available, navigating directly to create-content');
          const audioParam = finalAudioUrl || audioUrl;
          navigate(`/create-content/${projectId}?audioUrl=${encodeURIComponent(audioParam)}`);
        } else {
          //console.log('🎵 No audio available, opening audio selection modal');
          setIsAudioSelectionModalOpen(true);
        }
        break;
      case StepIndicatorNames.CaptionCreation:
        // Open audio selection modal for caption creation (same as ContentCreation)
        //console.log('🎵 Opening audio selection modal for caption creation');
        setIsAudioSelectionModalOpen(true);
        break;
      case StepIndicatorNames.TitleDescTag:
        // Navigate to title description for this project
        if (projectId) {
          //console.log('✅ Navigating to title description for project:', projectId);
          navigate(`/title-description/${projectId}`);
        }
        break;
      case StepIndicatorNames.ThumbnailCreation:
        // Navigate to thumbnail creation for this project
        if (projectId) {
          //console.log('✅ Navigating to thumbnail editor for project:', projectId);
          navigate(`/thumbnail-editor/${projectId}`);
        } else {
          //console.log('⚠️ No project ID, navigating to thumbnail landing');
          navigate('/thumbnail-landing');
        }
        break;
      case StepIndicatorNames.Export:
        // Handle export
        //console.log('🚧 Export functionality not implemented yet');
        break;
      default:
        console.log('❓ Unknown navigation key:', key);
        break;
    }
  };

  return (
    <div className={styles.audioEditorContainer}>
      <Navigation />
      <VerticalNav 
        handleNavSelected={handleNavSelected} 
        initialStep={StepIndicatorNames.AudioGeneration}
        projectData={projectData}
      />
      
      <div className={styles.editorContent}>
        {/* Header - Using AudioEditorHeader component */}
        <AudioEditorHeader
          projectTitle={projectTitle}
          isEditingProjectTitle={isEditingProjectTitle}
          onProjectTitleChange={setIsEditingProjectTitle}
          onProjectTitleUpdate={handleUpdateProjectTitle}
          onBackClick={() => navigate('/audio-generation')}
          onTitleClick={() => navigate('/audio-generation')}
          onAIAudioClick={() => handleOpenAIAudioModal('narration')}
          onUploadAudioClick={handleFileUpload}
          onSaveClick={saveProject}
          onMixAudioClick={() => {
            //console.log('🎵 Mix Audio button clicked!');
            setShowAudioMixer(true);
          }}
          onExportClick={finalAudioUrl ? exportAudio : undefined}
          isLoading={isLoading}
          hasTracks={tracks.length > 0}
          hasExportableAudio={!!finalAudioUrl}
          masterVolume={masterVolume}
          isMasterMuted={isMasterMuted}
          onMasterVolumeChange={setMasterVolume}
          onMasterMuteToggle={() => setIsMasterMuted(!isMasterMuted)}
        />

        <div className={styles.editorMain}>
          {/* Main Timeline Section */}
          <div className={styles.timelineSection}>
            {/* Timeline Controls - Using AudioControlBar component */}
            <AudioControlBar
              isPlaying={isPlaying}
              currentTime={currentTime}
              totalDuration={totalDuration}
              hasTracks={tracks.length > 0}
              onTogglePlayback={togglePlayback}
              onStop={() => {
                playingSourcesRef.current.forEach(source => {
                  try { source.stop(); } catch (e) {}
                });
                playingSourcesRef.current = [];
                setIsPlaying(false);
                setCurrentTime(0);
              }}
              formatTime={formatTime}
              selectedTrackId={selectedTrackId}
              selectedTrack={selectedTrack}
              onDuplicateTrack={duplicateTrack}
              onSplitTrack={splitTrack}
              onDeleteTrack={deleteTrack}
              onFillGaps={handleFillGaps}
              totalTracks={tracks.length}
              snapToGrid={snapToGrid}
              gridSize={gridSize}
              onSnapToggle={setSnapToGrid}
              onGridSizeChange={setGridSize}
              zoomLevel={zoomLevel}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              onResetZoom={resetZoom}
              autoCenter={autoCenter}
              onAutoCenterToggle={setAutoCenter}
            />

            {/* Main Timeline Area */}
            <div className={styles.timelineArea}>
              {/* Time ruler - Adaptive markers based on zoom */}
              <div 
                ref={timeRulerRef}
                className={styles.timeRuler}
                onScroll={handleRulerScroll}
              >
                <div 
                  style={{ 
                    width: `${timelineWidth}px`,
                    height: '100%',
                    position: 'relative',
                    minWidth: '100%'
                  }}
                >
                  {(() => {
                    const interval = getTimeMarkerInterval(zoomLevel);
                    const markerCount = Math.ceil(totalDuration / interval);
                    
                    return Array.from({ length: markerCount + 1 }, (_, i) => {
                      const timeValue = i * interval;
                      if (timeValue > totalDuration) return null;
                      
                      return (
                        <div
                          key={`marker-${i}-${interval}`}
                          className={styles.timeMarker}
                          style={{ left: `${timeValue * pixelsPerSecond * zoomLevel}px` }}
                        >
                          <span>{formatTimeMarker(timeValue, interval)}</span>
                        </div>
                      );
                    }).filter(Boolean);
                  })()}
                </div>
              </div>

              {/* Timeline Container */}
              <div 
                ref={timelineContainerRef}
                className={styles.timelineContainer}
                onScroll={handleTimelineScroll}
              >
                {/* Timeline - Fixed width based on zoom */}
                <div
                  ref={timelineRef}
                  className={styles.timeline}
                  onClick={handleTimelineClick}
                  style={{ 
                    width: `${timelineWidth}px`,
                    minWidth: '100%',
                    position: 'relative'
                  }}
                >
                  {/* Playhead - Fixed positioning */}
                  <div
                    ref={playheadRef}
                    className={styles.playhead}
                    style={{ left: `${currentTime * pixelsPerSecond * zoomLevel}px` }}
                  />

                  {/* Grid lines for snapping */}
                  {snapToGrid && Array.from({ length: Math.ceil(totalDuration / gridSize) + 1 }, (_, i) => (
                    <div
                      key={`grid-${i}`}
                      style={{
                        position: 'absolute',
                        left: `${i * gridSize * pixelsPerSecond * zoomLevel}px`,
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        background: 'rgba(59, 130, 246, 0.2)',
                        pointerEvents: 'none'
                      }}
                    />
                  ))}

                  {/* Audio Tracks */}
                  {[0, 1, 2].map(laneIndex => (
                    <div
                      key={laneIndex}
                      className={styles.trackLane}
                      style={{ 
                        top: `${laneIndex * 90}px`,
                        width: '100%',
                        height: '85px'
                      }}
                    >
                      <div className={styles.laneLabel}>Track {laneIndex + 1}</div>
                      
                      {/* Track Lane Background */}
                      <div className={styles.laneBackground} />
                      
                      {
                      tracks
                        .filter(track => {
                          const normalizedLane = Math.max(0, Math.min(track.laneIndex ?? 0, 2));
                          console.log(`🎯 RENDER FILTER: Track "${track.name}" - originalLane: ${track.laneIndex}, normalizedLane: ${normalizedLane}, targetLane: ${laneIndex}, matches: ${normalizedLane === laneIndex}`);
                          return normalizedLane === laneIndex;
                        }) // ✅ Force valid lane filtering
                        .map(track => {
                          const trackState = trackLoadStates.get(track.id) || 'DORMANT';
                          const isDormant = trackState === 'DORMANT';
                          const isLoading = trackState === 'LOADING';
                          const isError = trackState === 'ERROR';
                          
                          return (
                            <div
                              key={track.id}
                              className={`${styles.audioTrack} ${track.isSelected ? styles.selected : ''} ${isDormant ? styles.dormant : ''}`}
                              style={{
                                left: `${track.startTime * pixelsPerSecond * zoomLevel}px`,
                                width: `${track.duration * pixelsPerSecond * zoomLevel}px`,
                                top: '5px', // ✅ Override CSS top with consistent positioning within lane
                                backgroundColor: isDormant ? '#6b7280' : (isError ? '#ef4444' : track.color),
                                opacity: isDormant ? 0.4 : (isLoading ? 0.7 : (track.isMuted ? 0.5 : (draggedTrack === track.id ? 0.8 : 1))),
                                border: isError ? '2px solid #ef4444' : (draggedTrack === track.id ? '2px dashed rgba(59, 130, 246, 0.8)' : (isDormant ? '2px dashed #6b7280' : '2px solid transparent')),
                                cursor: isDormant ? 'pointer' : 'grab',
                                userSelect: 'none',
                                transition: draggedTrack === track.id ? 'none' : 'all 0.1s ease',
                                position: 'absolute', // ✅ Need absolute for left positioning within lane
                              }}
                              onClick={isDormant ? () => handleTrackActivation(track.id) : undefined}
                              onMouseDown={isDormant ? undefined : (e) => handleTrackMouseDown(track.id, e)}
                              ref={(el) => {
                                if (el) {
                                    console.log('🎵 TRACK RENDER:', {
                                    name: track.name,
                                    fadeInDuration: track.fadeInDuration,
                                    fadeOutDuration: track.fadeOutDuration,
                                    duration: track.duration,
                                    shouldShowFadeIn: track.fadeInDuration > 0,
                                    shouldShowFadeOut: track.fadeOutDuration > 0,
                                    fadeInWidth: track.fadeInDuration ? `${(track.fadeInDuration / track.duration) * 100}%` : '0%',
                                    fadeOutWidth: track.fadeOutDuration ? `${(track.fadeOutDuration / track.duration) * 100}%` : '0%'
                                  });
                                }
                              }}
                            >
                              <div className={styles.trackContent}>
                                {/* ✅ DORMANT STATE OVERLAY */}
                                {isDormant && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(0, 0, 0, 0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    zIndex: 10,
                                    cursor: 'pointer',
                                  }}>
                                    💤 Click to Load
                                  </div>
                                )}
                                
                                {/* ✅ LOADING STATE OVERLAY */}
                                {isLoading && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(59, 130, 246, 0.8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    zIndex: 10,
                                  }}>
                                    ⏳ Loading...
                                  </div>
                                )}
                                
                                {/* ✅ ERROR STATE OVERLAY */}
                                {isError && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(239, 68, 68, 0.8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    zIndex: 10,
                                    cursor: 'pointer',
                                  }}>
                                    ❌ Click to Retry
                                  </div>
                                )}

                                <div className={styles.trackName}>{track.name}</div>
                                <div className={styles.trackWaveform}>
                                {/* ✅ SHOW PLACEHOLDER WAVEFORM FOR DORMANT TRACKS */}
                                {Array.from({ length: Math.max(5, Math.floor(track.duration * 10)) }, (_, i) => (
                                  <div
                                    key={`${track.id}-waveform-${i}`}
                                    className={styles.waveformBar}
                                    style={{ 
                                      height: isDormant ? `${20 + (i % 3) * 20}%` : `${Math.random() * 80 + 20}%`,
                                      opacity: isDormant ? 0.3 : 1
                                    }}
                                  />
                                ))}
                              </div>
                            </div>

                            {/* Fade In Overlay */}
                            {track.fadeInDuration > 0 && (
                              <div
                                className={styles.fadeInOverlay}
                                style={{
                                  width: `${(track.fadeInDuration / track.duration) * 100}%`,
                                  background: 'linear-gradient(to right, rgba(0, 0, 0, 0.7), transparent)',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  height: '100%',
                                  pointerEvents: 'none',
                                  borderTopLeftRadius: '10px',
                                  borderBottomLeftRadius: '10px',
                                  zIndex: 10,
                                  border: '2px solid red' // DEBUG: Make it visible
                                }}
                                onMouseEnter={() => console.log('🔴 FADE IN DEBUG:', {
                                  trackName: track.name,
                                  fadeInDuration: track.fadeInDuration,
                                  trackDuration: track.duration,
                                  widthPercentage: (track.fadeInDuration / track.duration) * 100,
                                  borderRadius: '10px',
                                  shouldBeVisible: track.fadeInDuration > 0
                                })}
                              >
                                <div className={styles.fadeOverlayLabel}>Fade In</div>
                              </div>
                            )}

                            {/* Fade Out Overlay */}
                            {track.fadeOutDuration > 0 && (
                              <div
                                className={styles.fadeOutOverlay}
                                style={{
                                  width: `${(track.fadeOutDuration / track.duration) * 100}%`,
                                  background: 'linear-gradient(to left, rgba(0, 0, 0, 0.7), transparent)',
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  height: '100%',
                                  pointerEvents: 'none',
                                  borderTopRightRadius: '10px',
                                  borderBottomRightRadius: '10px',
                                  zIndex: 10,
                                  border: '2px solid blue' // DEBUG: Make it visible
                                }}
                                onMouseEnter={() => console.log('🔵 FADE OUT DEBUG:', {
                                  trackName: track.name,
                                  fadeOutDuration: track.fadeOutDuration,
                                  trackDuration: track.duration,
                                  widthPercentage: (track.fadeOutDuration / track.duration) * 100,
                                  borderRadius: '10px',
                                  shouldBeVisible: track.fadeOutDuration > 0
                                })}
                              >
                                <div className={styles.fadeOverlayLabel}>Fade Out</div>
                              </div>
                            )}
                            
                            <div 
                              className={styles.resizeHandleLeft}
                              onMouseDown={(e) => handleResizeMouseDown(track.id, 'left', e)}
                              style={{ cursor: 'ew-resize' }}
                            />
                            <div 
                              className={styles.resizeHandleRight}
                              onMouseDown={(e) => handleResizeMouseDown(track.id, 'right', e)}
                              style={{ cursor: 'ew-resize' }}
                            />
                          </div>
                          ); // ✅ FIX: Added closing for return statement
                        })
                      } 
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Always Visible Tabbed Interface */}
          <TabContainer
            selectedTrack={selectedTrack}
            currentTime={currentTime}
            onVolumeChange={updateTrackVolume}
            onMuteToggle={toggleTrackMute}
            onDuplicate={duplicateTrack}
            onSplit={splitTrack}
            onDelete={deleteTrack}
            onFadeChange={(trackId, property, value) => {
              console.log('🎛️ FADE CHANGE:', {
                trackId,
                property,
                value,
                timestamp: new Date().toISOString()
              });
              
              setTracks(prev => prev.map(track => {
                if (track.id === trackId) {
                  const updatedTrack = { ...track, [property]: value };
                  console.log('🎵 UPDATED TRACK:', {
                    name: updatedTrack.name,
                    fadeInDuration: updatedTrack.fadeInDuration,
                    fadeOutDuration: updatedTrack.fadeOutDuration,
                    duration: updatedTrack.duration,
                    fadeInPercent: updatedTrack.fadeInDuration ? (updatedTrack.fadeInDuration / updatedTrack.duration) * 100 : 0,
                    fadeOutPercent: updatedTrack.fadeOutDuration ? (updatedTrack.fadeOutDuration / updatedTrack.duration) * 100 : 0
                  });
                  return updatedTrack;
                }
                return track;
              }));
            }}
            onSoundEffectAdd={handleSoundEffectAdd}
            onOpenSoundEffectsModal={handleOpenSoundEffectsModal}
            userId={user?.id || ''}
            projectId={projectId} // Pass projectId to TabContainer
            refreshTrigger={soundEffectsRefreshTrigger}
          />
        </div>

        {/* Empty State */}
        {tracks.length === 0 && !isLoading && (
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faUpload} size="3x" color="#ccc" />
            <h3>No Audio Tracks</h3>
            <p>Upload audio files to start editing</p>
            <Button
              leftSection={<FontAwesomeIcon icon={faUpload} />}
              onClick={handleFileUpload}
              size="lg"
              className={styles.emptyStateButton}
            >
              Upload Audio Files
            </Button>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Audio Mixer Modal */}
      <AudioMixer
        tracks={tracks}
        totalDuration={totalDuration}
        masterVolume={masterVolume}
        isMasterMuted={isMasterMuted}
        isVisible={showAudioMixer}
        onClose={() => setShowAudioMixer(false)}
        onMixComplete={(audioUrl) => {
          //console.log('🎯 Audio mix completed, updating finalAudioUrl:', audioUrl);
          setFinalAudioUrl(audioUrl);
        }}
        projectId={projectId}
      />

      {/* Sound Effects Generation Modal */}
      <SoundEffectsModal
        isOpen={showSoundEffectsModal}
        onClose={() => setShowSoundEffectsModal(false)}
        onSoundEffectGenerated={handleSoundEffectGenerated}
        userId={user?.id || ''}
      />

      {/* AI Audio Generation Modal */}
      <AudioModal
        isOpen={isAudioModalOpen}
        onClose={() => setIsAudioModalOpen(false)}
        onAudioGenerated={handleAudioGenerated}
        type={audioModalType}
        currentProjectId={projectId} // Use the renamed prop
      />

      {/* Audio Selection Modal for Caption Creation */}
      {/* Debug: Rendering AudioSelectionModal with isOpen: {isAudioSelectionModalOpen}, projectId: {projectId || ''}, finalAudioUrl: {finalAudioUrl}, originalAudioUrl: {originalAudioUrl} */}
      
      {/* Audio Selection Modal for Caption Creation */}
      <AudioSelectionModal
        isOpen={isAudioSelectionModalOpen}
        onClose={() => setIsAudioSelectionModalOpen(false)}
        projectId={projectId || ''}
        finalAudioUrl={finalAudioUrl}
        originalAudioUrl={originalAudioUrl}
        projectTitle={projectTitle}
        tracks={tracks}
        onGenerateAudio={() => {
          //console.log('🎵 Triggering audio generation from content creation modal');
          
          // Check if there are tracks to mix
          if (tracks.length === 0) {
            showNotification({
              title: 'No Audio Tracks',
              message: 'Please add some audio tracks before generating audio. Use the AI Audio or Sound Effects features to create content.',
              type: 'error'
            });
            return;
          }
          
          setShouldNavigateToContentCreation(true); // Set flag to navigate after generation
          setShowAudioMixer(true); // Open the Mix Audio modal
        }}
      />
    </div>
  );
};

export default AudioEditorSplit;
